/**
 * Типи задач з `/meta` (публічна частина прив'язки `activities_task`, tasks.md, розд. 5) — без
 * окремого запиту й без рантайму `@clientline/contracts`: файл бере й перший екран («Сьогодні»
 * вирішує, чи пропонувати «Виконати з результатом…») і картка («Записати як виконану»), тож zod і
 * код панелі сюди не тягнемо. Файл лежить у першому екрані навмисно: модуль, спільний для картки й
 * лінивого чанка панелі, Rolldown інакше поклав би в `routes`, і панель тягнула б +38 КБ gzip.
 */
import type { ActivityOutcomeMeta, ActivityTypeMeta, MetaEntity } from '@clientline/contracts';
import { fieldOf } from '../../../platform/meta.ts';

/** Ключ прив'язки поведінки задач на сутності (`/meta`, `entity.behaviors`). */
export const TASK_BEHAVIOR = 'activities_task';

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/**
 * Типи задач сутності. Перевірка мінімальна, без zod: тип без ключа, категорії чи переліку
 * результатів відкидається, а невалідна прив'язка цілком — `null` (панелі немає, fail closed).
 */
export function taskTypesOf(entity: MetaEntity | undefined): ActivityTypeMeta[] | null {
  const raw = entity?.behaviors?.[TASK_BEHAVIOR];
  if (!isObject(raw) || !Array.isArray(raw.types)) return null;
  return raw.types.filter(
    (t): t is ActivityTypeMeta =>
      isObject(t) &&
      typeof t.key === 'string' &&
      typeof t.category === 'string' &&
      Array.isArray(t.outcomes),
  );
}

export function typeOf(
  types: readonly ActivityTypeMeta[] | null,
  key: string,
): ActivityTypeMeta | null {
  return types?.find((t) => t.key === key) ?? null;
}

/** Є з чого обирати: панель має сенс лише для типу з активними результатами. */
export function hasOutcomes(type: ActivityTypeMeta | null): boolean {
  return Boolean(type?.outcomes.some((o) => !o.inactive));
}

/** Цифри чипів результату: не більше 9 активних результатів на тип (tasks.md, розд. 5). */
export const OUTCOME_CHIPS_MAX = 9;

/** Результати для нового вибору: активні, у порядку типу, до 9 (цифри `1`…`9`). */
export function outcomeChoices(type: ActivityTypeMeta): ActivityOutcomeMeta[] {
  return type.outcomes.filter((o) => !o.inactive).slice(0, OUTCOME_CHIPS_MAX);
}

/** Результати для запису (TSK-10): без `nextStep: required` — крок в одиниці запису не буває. */
export function logOutcomes(type: ActivityTypeMeta): ActivityOutcomeMeta[] {
  return outcomeChoices(type).filter((o) => o.nextStep !== 'required');
}

/**
 * Типи для запису: активні, у порядку показу. Тип з обов'язковим результатом, у якого лишились самі
 * результати з `nextStep: required`, записати не вийде, тож він не пропонується.
 */
export function logTypes(types: readonly ActivityTypeMeta[] | null): ActivityTypeMeta[] {
  return (types ?? []).filter(
    (t) => !t.inactive && (!t.outcomeRequired || logOutcomes(t).length > 0),
  );
}

/** Поведінка господаря активностей на сутності запису (`/meta`, `entity.behaviors`). */
export const LOG_HOST_BEHAVIOR = 'activities';

/**
 * «Записати як виконану» доступне (рішення Архітектора 22.10): сутність задач можна створювати, роль
 * має `edit` на `state`, `completed_at` і тему (tasks.md, розд. 6, «Рівні полів»), є тип для запису.
 * Інакше — `false`, режиму немає (fail closed). `host` — сутність запису, на якому записуємо: вона
 * має приймати активності (поведінка `activities`).
 */
export function logAvailable(activity: MetaEntity | undefined, host?: MetaEntity): boolean {
  if (!activity?.canCreate) return false;
  if (host && !(LOG_HOST_BEHAVIOR in (host.behaviors ?? {}))) return false;
  const editable = (key: string) => fieldOf(activity, key)?.edit === true;
  if (!editable('state') || !editable('completed_at') || !editable('subject')) return false;
  return logTypes(taskTypesOf(activity)).length > 0;
}
